import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import deviceGroupService from "../services/deviceGroupService";
import macPackageService from "../services/macPackageService";

const emptyForm = () => ({
  name: "",
  description: "",
  title: "",
  bundleIdentifier: "",
  bundleVersion: "1.0",
  removeFilesOnUninstall: true,
  installRequired: true,
  deliveryMode: "auto",
  enabled: true,
  groups: [],
  udidsText: "",
});

function formFromPackage(row) {
  return {
    name: row.name || "",
    description: row.description || "",
    title: row.title || "",
    bundleIdentifier: row.bundleIdentifier || "",
    bundleVersion: row.bundleVersion || "1.0",
    removeFilesOnUninstall: row.removeFilesOnUninstall !== false,
    installRequired: row.installRequired !== false,
    deliveryMode: row.deliveryMode || "auto",
    enabled: row.enabled !== false,
    groups: (row.groups || []).map((g) => g._id || g).filter(Boolean),
    udidsText: (row.udids || []).join("\n"),
  };
}

function payloadFromForm(form) {
  const udids = String(form.udidsText || "")
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    name: form.name,
    description: form.description,
    title: form.title || form.name,
    bundleIdentifier: form.bundleIdentifier,
    bundleVersion: form.bundleVersion,
    removeFilesOnUninstall: form.removeFilesOnUninstall,
    installRequired: form.installRequired,
    deliveryMode: form.deliveryMode,
    enabled: form.enabled,
    groups: form.groups,
    udids,
  };
}

export default function MacPackages() {
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, groupList] = await Promise.all([
        macPackageService.listPackages(),
        deviceGroupService.getDeviceGroups().catch(() => []),
      ]);
      setRows(list);
      setGroups(Array.isArray(groupList) ? groupList : []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleGroup = (id) => {
    setForm((prev) => {
      const set = new Set(prev.groups.map(String));
      const key = String(id);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, groups: [...set] };
    });
  };

  const startCreate = () => {
    setEditingId("new");
    setForm(emptyForm());
    setFile(null);
    setUploadPercent(null);
  };

  const startEdit = (row) => {
    setEditingId(row._id);
    setForm(formFromPackage(row));
    setFile(null);
    setUploadPercent(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFile(null);
    setUploadPercent(null);
  };

  const save = async () => {
    if (!admin) return;
    setSaving(true);
    setUploadPercent(file ? 0 : null);
    try {
      const payload = payloadFromForm(form);
      let row;
      if (editingId === "new") {
        row = await macPackageService.createPackage(payload);
        if (file) {
          row = await macPackageService.uploadPackageFile(row._id, file, {
            onProgress: ({ percent }) => setUploadPercent(percent),
          });
        }
        toast.success(file ? "Paket angelegt und Datei hochgeladen" : "Paket angelegt — bitte .pkg hochladen");
      } else {
        row = await macPackageService.updatePackage(editingId, payload);
        if (file) {
          row = await macPackageService.uploadPackageFile(editingId, file, {
            onProgress: ({ percent }) => setUploadPercent(percent),
          });
        }
        toast.success(file ? "Datei hochgeladen / gespeichert" : "Paket gespeichert / synchronisiert");
      }
      cancelEdit();
      await load();
      return row;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
      setUploadPercent(null);
    }
  };

  const publish = async (id) => {
    if (!admin) return;
    try {
      const result = await macPackageService.publishPackage(id);
      const errs = result?.sync?.errors || [];
      if (errs.length) toast.warning(`Sync mit Hinweisen: ${errs[0]}`);
      else toast.success("Paket an Ziele ausgerollt");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Publish fehlgeschlagen");
    }
  };

  const remove = async (id) => {
    if (!admin) return;
    if (!window.confirm("Paket von Geräten entfernen und Datensatz löschen?")) return;
    try {
      await macPackageService.deletePackage(id);
      toast.success("Paket entfernt");
      if (editingId === id) cancelEdit();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Mac-Pakete</h2>
          <p className="text-muted mb-0 small">
            Signierte und notarized <code>.pkg</code>-Dateien hochladen. Xavier erzeugt Manifest und
            Chunk-Hashes. URLs nutzen eine UUID + HMAC. macOS&nbsp;26+: DDM Package;
            Datei-Rollback (<code>UninstallBehavior</code>) nur wenn alle Ziele ≥&nbsp;27.
            Ältere Macs: InstallEnterpriseApplication.
          </p>
        </div>
        {admin ? (
          <button type="button" className="btn btn-primary" onClick={startCreate} disabled={!!user && saving}>
            Neues Paket
          </button>
        ) : null}
      </div>

      {editingId ? (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId === "new" ? "Paket anlegen" : "Paket bearbeiten"}</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input className="form-control" value={form.name} onChange={(e) => setField("name", e.target.value)} disabled={!admin} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Titel (Manifest)</label>
                <input className="form-control" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={!admin} placeholder="optional, sonst Name" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Bundle-Identifier</label>
                <input className="form-control" value={form.bundleIdentifier} onChange={(e) => setField("bundleIdentifier", e.target.value)} disabled={!admin} placeholder="de.beispiel.paket" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Version</label>
                <input className="form-control" value={form.bundleVersion} onChange={(e) => setField("bundleVersion", e.target.value)} disabled={!admin} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Auslieferung</label>
                <select className="form-select" value={form.deliveryMode} onChange={(e) => setField("deliveryMode", e.target.value)} disabled={!admin}>
                  <option value="auto">Auto (DDM ab 26, sonst MDM)</option>
                  <option value="ddm">Nur DDM</option>
                  <option value="mdm">Nur InstallEnterpriseApplication</option>
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Beschreibung</label>
                <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setField("description", e.target.value)} disabled={!admin} />
              </div>
              <div className="col-md-4">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pkgEnabled" checked={form.enabled} onChange={(e) => setField("enabled", e.target.checked)} disabled={!admin} />
                  <label className="form-check-label" htmlFor="pkgEnabled">Aktiv (Zuweisung anwenden)</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pkgRequired" checked={form.installRequired} onChange={(e) => setField("installRequired", e.target.checked)} disabled={!admin} />
                  <label className="form-check-label" htmlFor="pkgRequired">Installation erforderlich (DDM)</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="pkgRemove" checked={form.removeFilesOnUninstall} onChange={(e) => setField("removeFilesOnUninstall", e.target.checked)} disabled={!admin} />
                  <label className="form-check-label" htmlFor="pkgRemove">Dateien bei Deinstall entfernen (DDM, nur macOS&nbsp;27+)</label>
                </div>
                <div className="form-text">Auf macOS&nbsp;26 lässt Xavier UninstallBehavior weg (sonst invalid payload). File-Tracking erst ab 27.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Geräte-Gruppen</label>
                <div className="border rounded p-2" style={{ maxHeight: 160, overflow: "auto" }}>
                  {groups.length ? groups.map((g) => (
                    <div className="form-check" key={g._id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`pkg-g-${g._id}`}
                        checked={form.groups.map(String).includes(String(g._id))}
                        onChange={() => toggleGroup(g._id)}
                        disabled={!admin}
                      />
                      <label className="form-check-label" htmlFor={`pkg-g-${g._id}`}>{g.name}</label>
                    </div>
                  )) : <span className="text-muted small">Keine Gruppen</span>}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Zusätzliche UDIDs</label>
                <textarea className="form-control font-monospace small" rows={5} value={form.udidsText} onChange={(e) => setField("udidsText", e.target.value)} disabled={!admin} placeholder="eine UDID pro Zeile" />
              </div>
              <div className="col-12">
                <label className="form-label">.pkg-Datei (signiert + notarized)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pkg,application/octet-stream"
                  disabled={!admin || saving}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <div className="form-text">
                  Upload in 4‑MiB-Chunks mit Fortschrittsanzeige. Xavier erzeugt Manifest und Prüfsummen selbst.
                  Einträge ohne Datei: Bearbeiten und nur die .pkg erneut wählen.
                </div>
                {uploadPercent != null ? (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>Upload…</span>
                      <span>{uploadPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${uploadPercent}%` }}
                        aria-valuenow={uploadPercent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="button" className="btn btn-primary" onClick={save} disabled={!admin || saving}>
                {saving
                  ? (uploadPercent != null ? `Hochladen ${uploadPercent}%…` : "Speichern…")
                  : "Speichern"}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit} disabled={saving}>Abbrechen</button>
            </div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="text-muted">Laden…</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Bundle / Version</th>
                <th>Datei</th>
                <th>Ziele</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id}>
                  <td>
                    <div className="fw-semibold">{row.name}</div>
                    <div className="small text-muted font-monospace">{row.publicId}</div>
                  </td>
                  <td className="small">
                    <div>{row.bundleIdentifier}</div>
                    <div className="text-muted">{row.bundleVersion}</div>
                  </td>
                  <td className="small">
                    {row.hasFile ? (
                      <span className="text-success">{row.originalFilename || "ok"} ({Math.round((row.fileSize || 0) / 1024)} KB)</span>
                    ) : (
                      <span className="text-warning">fehlt</span>
                    )}
                  </td>
                  <td className="small">
                    {row.targetCount ?? 0} Ziel(e)
                    {(row.groupNames || []).length ? (
                      <div className="text-muted">{row.groupNames.join(", ")}</div>
                    ) : null}
                    <div className="text-muted">angewendet: {(row.appliedUdids || []).length}</div>
                  </td>
                  <td className="small">
                    {row.enabled === false ? <span className="text-muted">inaktiv</span> : <span>aktiv</span>}
                    {row.lastError ? <div className="text-danger">{row.lastError}</div> : null}
                  </td>
                  <td className="text-nowrap">
                    {admin ? (
                      <div className="d-flex gap-1 flex-wrap">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startEdit(row)}>Bearbeiten</button>
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => publish(row._id)} disabled={!row.hasFile}>Publish</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(row._id)}>Löschen</button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={6} className="text-muted">Noch keine Pakete.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
